import { NavLink } from "react-router-dom";

import { cn } from "@/lib/utils";
import { AdminScopeSelector } from "@/components/layout/AdminScopeSelector";
import type { AdminScope, NavItem } from "@/components/layout/nav-config";

interface SidebarNavProps {
  items: NavItem[];
  /** Fired after a nav item is chosen (used to close the mobile drawer). */
  onNavigate?: () => void;
  /**
   * Story 15.7 AC16 — the admin-scope block, rendered ABOVE the nav list where
   * all seven admin mockups put it. Supplied for a super_admin ONLY; every other
   * role has one estate and nothing to switch between, so the block is absent
   * rather than disabled.
   *
   * It lives HERE rather than in `Sidebar`'s `<aside>` because `MobileNavDrawer`
   * renders this same component on its own path — a selector added to the
   * desktop aside alone would leave every viewport at or below 1120px without
   * one. One insertion point, both breakpoints.
   */
  adminScope?: AdminScope;
  /** The current path, for the scope hint. Required whenever `adminScope` is set. */
  pathname?: string;
}

// Tooltip shown on a locked nav item (Story 16.2 / Screen 14b) — explains BOTH
// gates so the copy still reads true at stage-2 (subscription) once 16.7 lands.
const LOCKED_TOOLTIP =
  "Locked until your certificate is verified and your subscription is active.";

/** The nav list itself. Split out so the scope block above it stays legible. */
function NavList({ items, onNavigate }: Pick<SidebarNavProps, "items" | "onNavigate">) {
  return (
    <nav aria-label="Primary" className="flex flex-col gap-[2px]">
      {items.map((item) => {
        const Icon = item.icon;
        // Story 16.2 — a locked item (gated legacy clinician) renders as a
        // NON-interactive span: no NavLink, no href, not keyboard-focusable, so
        // it cannot navigate. aria-disabled + the `· locked` suffix (not
        // colour-only) carry the state to assistive tech. The `title` is
        // mouse-hover-only, so the "why" is also mirrored into the accessible
        // name via sr-only text — AT users hear the reason, not just "locked".
        if (item.locked) {
          return (
            <span
              key={item.to}
              aria-disabled="true"
              title={LOCKED_TOOLTIP}
              className="flex min-h-[44px] cursor-not-allowed items-center gap-3 rounded-[var(--radius-sm)] px-3 py-2 text-sm font-semibold text-[color:var(--text-tertiary)] opacity-50"
            >
              <Icon aria-hidden="true" className="h-[18px] w-[18px] shrink-0" />
              <span>{item.label} · locked</span>
              <span className="sr-only">{LOCKED_TOOLTIP}</span>
            </span>
          );
        }
        return (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                "flex min-h-[44px] items-center gap-3 rounded-[var(--radius-sm)] px-3 py-2 text-sm font-semibold transition-colors",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--focus-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--bg-secondary)]",
                isActive
                  ? "bg-[color:var(--bg-tertiary)] text-[color:var(--text-primary)]"
                  : "text-[color:var(--text-tertiary)] hover:bg-[color:var(--bg-tertiary)] hover:text-[color:var(--text-primary)]",
              )
            }
          >
            <Icon aria-hidden="true" className="h-[18px] w-[18px] shrink-0" />
            <span>{item.label}</span>
          </NavLink>
        );
      })}
    </nav>
  );
}

// Shared nav, rendered both in the desktop sidebar and the mobile drawer.
// NavLink sets aria-current="page" on the active route automatically, satisfying
// "active item indicated by more than colour" (AC 6).
export function SidebarNav({ items, onNavigate, adminScope, pathname }: SidebarNavProps) {
  if (!adminScope) return <NavList items={items} onNavigate={onNavigate} />;
  return (
    <div className="flex flex-col gap-5">
      <AdminScopeSelector scope={adminScope} pathname={pathname ?? ""} onNavigate={onNavigate} />
      <NavList items={items} onNavigate={onNavigate} />
    </div>
  );
}
